import _extends from "@babel/runtime/helpers/extends";

/** @jsx jsx */
import { Children, createRef, useCallback, useMemo } from 'react';
import { jsx } from '@emotion/core';
import GlobalTheme from '@atlaskit/theme/components';
import { useTabList } from '../hooks';
import { TabContext } from '../internal/context';
import { getTabListStyles } from '../internal/styles';
import { onMouseDownBlur } from '../internal/utils';

var TabListWithMode = function TabListWithMode(props) {
  var children = props.children,
      mode = props.mode;

  var _useTabList = useTabList(),
      tabsId = _useTabList.tabsId,
      selected = _useTabList.selected,
      onChange = _useTabList.onChange;

  var ref = /*#__PURE__*/createRef(); // Don't include any conditional children

  var childrenArray = Children.toArray(children).filter(Boolean);
  var length = childrenArray.length;
  var styles = useMemo(function () {
    return getTabListStyles(mode);
  }, [mode]);
  var onKeyDown = useCallback(function (e) {
    var _ref$current;

    if (!['ArrowRight', 'ArrowLeft'].includes(e.key)) {
      return;
    } // We use aria-posinset so we don't rely on the selected variable
    // If we used the selected variable this would regenerate each time
    // and create an unstable reference


    var selectedIndex = parseInt(e.currentTarget.getAttribute('aria-posinset') || '0') - 1;
    var modifier = e.key === 'ArrowRight' ? 1 : -1;
    var newSelected = selectedIndex + modifier;

    if (newSelected < 0 || newSelected >= length) {
      return;
    }

    var newSelectedNode = (_ref$current = ref.current) === null || _ref$current === void 0 ? void 0 : _ref$current.querySelector("[id='".concat(tabsId, "-").concat(newSelected, "']"));

    if (newSelectedNode) {
      newSelectedNode.focus();
    }

    onChange(newSelected);
  }, [length, onChange, tabsId, ref]); // Memoized so the function isn't recreated each time

  var getTabWithContext = useCallback(function (_ref) {
    var tab = _ref.tab,
        isSelected = _ref.isSelected,
        index = _ref.index;
    return jsx(TabContext.Provider, {
      value: {
        onClick: function onClick() {
          return onChange(index);
        },
        onMouseDown: onMouseDownBlur,
        onKeyDown: onKeyDown,
        'aria-setsize': length,
        role: 'tab',
        id: "".concat(tabsId, "-").concat(index),
        'aria-posinset': index + 1,
        'aria-selected': isSelected,
        'aria-controls': "".concat(tabsId, "-").concat(index, "-tab"),
        tabIndex: isSelected ? 0 : -1
      },
      key: index
    }, tab);
  }, [length, onKeyDown, onChange, tabsId]);
  return jsx("div", {
    role: "tablist",
    css: styles,
    ref: ref
  }, childrenArray.map(function (child, index) {
    return getTabWithContext({
      tab: child,
      index: index,
      isSelected: index === selected
    });
  }));
};

export var TabList = function TabList(props) {
  return jsx(GlobalTheme.Consumer, null, function (_ref2) {
    var mode = _ref2.mode;
    return jsx(TabListWithMode, _extends({}, props, {
      mode: mode
    }));
  });
};
export default TabList;